import React, { useEffect, useState } from 'react';

import { Approvals, Transfer } from './GovernancePanel';
import HealthPanel from './HealthPanel';
import PromptsPanel from './PromptsPanel';
import SettingsEditor from './SettingsEditor';
import PipelineSettingsPanel, {
  Notice,
  Section,
  badge,
  errorText,
  formatDate,
  mono,
} from './PipelineSettingsPanel';
import { useAccess } from '../../services/access';
import { configApi } from '../../services/api';

// Admin → Configuration. Application-wide settings for every project:
// an overview, the meeting pipeline, and read-only views of the feature
// flags and the access/data policies in force. Needs admin:config:view.

const AREA_LABEL = {
  meetings: 'Meetings',
  mail: 'Mail',
  assistant: 'Assistant',
  dashboards: 'Dashboards',
  cost: 'Cost',
  observability: 'Observability',
  ai_models: 'AI models',
};

const AREA_HINT = {
  mail: 'Gate, extraction, drafting and sync thresholds.',
  assistant: 'Chat history, memory and Teams reply context.',
  dashboards: 'How fresh widget data is.',
  cost: 'Markup on the cost recorded for each model call.',
  observability: 'Links shown on the Health page.',
  meetings: 'Pipeline thresholds, context budgets and tracker cadence.',
};

// `reload` refetches when it changes (e.g. after a publish).
const useLoad = (fetcher, token, reload = 0) => {
  const [state, setState] = useState({ data: null, error: '' });
  useEffect(() => {
    if (!token) return;
    fetcher(token)
      .then((data) => setState({ data, error: '' }))
      .catch((e) => setState({ data: null, error: errorText(e) }));
  }, [fetcher, token, reload]);
  return state;
};

const Loading = ({ state, children }) => {
  if (state.error) return <Notice>{state.error}</Notice>;
  if (!state.data) return <p className="text-sm text-zinc-500">Loading…</p>;
  return children(state.data);
};

const Stat = ({ label, value, hint, onClick }) => {
  const body = (
    <>
      <div className="text-sm text-zinc-400">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-zinc-100">
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-zinc-400">{hint}</div>}
    </>
  );
  const frame =
    'rounded-lg border border-zinc-800 bg-zinc-900/50 p-4 text-left';
  return onClick ? (
    <button
      onClick={onClick}
      className={`${frame} transition-colors hover:border-zinc-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500`}
    >
      {body}
    </button>
  ) : (
    <div className={frame}>{body}</div>
  );
};

const ChangeList = ({ changes }) =>
  changes.length === 0 ? (
    <p className="text-sm text-zinc-500">No changes recorded yet.</p>
  ) : (
    <ul className="divide-y divide-zinc-800">
      {changes.map((c, i) => (
        <li key={`${c.at}-${i}`} className="py-2 text-sm">
          <span className="text-zinc-200">
            {AREA_LABEL[c.area] || c.area} · {c.subject}
          </span>{' '}
          <span className={`${mono} text-zinc-400`}>
            {c.action} {c.version || ''}
          </span>
          <div className="text-zinc-500">
            {formatDate(c.at)} · {c.changed_by || 'unknown'}
            {c.keys?.length ? ` · ${c.keys.join(', ')}` : ''}
          </div>
          {c.rationale && <div className="text-zinc-400">{c.rationale}</div>}
        </li>
      ))}
    </ul>
  );

const Overview = ({ token, openSettings }) => {
  const state = useLoad(configApi.overview, token);
  return (
    <Loading state={state}>
      {(data) => (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            {data.areas.map((a) => (
              <Stat
                key={a.area}
                label={`${AREA_LABEL[a.area] || a.area} settings`}
                value={`${a.changed} / ${a.settings} changed`}
                hint={`${a.live} of ${a.settings} read by running code`}
                onClick={() => openSettings(a.area)}
              />
            ))}
            <Stat
              label="Feature flags"
              value={`${data.switches.on} / ${data.switches.total} on`}
              hint={`${data.switches.security} security flags · set in code`}
            />
            <Stat
              label="Settings version"
              value={data.settings_version ? `v${data.settings_version}` : '—'}
              hint="Every publish is a new version"
            />
          </div>
          <Section
            title="Recent changes"
            hint="Settings, prompts, rubrics and AI models, newest first."
          >
            <ChangeList changes={data.recent_changes} />
          </Section>
        </div>
      )}
    </Loading>
  );
};

const Switches = ({ token }) => {
  const state = useLoad(configApi.switches, token);
  const [query, setQuery] = useState('');
  return (
    <Loading state={state}>
      {(data) => {
        const q = query.trim().toLowerCase();
        const flags = data.flags.filter(
          (f) =>
            !q ||
            f.name.toLowerCase().includes(q) ||
            f.description.toLowerCase().includes(q),
        );
        return (
          <div className="space-y-4">
            <Notice tone="zinc">
              Feature flags are set in code for each deployment and cannot be
              changed here.
            </Notice>
            <input
              className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100"
              placeholder="Filter flags"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <ul className="divide-y divide-zinc-800 rounded-lg border border-zinc-800">
              {flags.map((f) => (
                <li key={f.name} className="p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`${mono} text-zinc-200`}>{f.name}</span>
                    <span
                      className={`${badge} ${
                        f.value === false
                          ? 'bg-zinc-800 text-zinc-400'
                          : 'bg-emerald-500/10 text-emerald-300'
                      }`}
                    >
                      {String(f.value)}
                    </span>
                    {f.security && (
                      <span className={`${badge} bg-red-500/10 text-red-300`}>
                        security
                      </span>
                    )}
                  </div>
                  {f.description && (
                    <p className="mt-1 whitespace-pre-line text-xs text-zinc-500">
                      {f.description}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      }}
    </Loading>
  );
};

const Policies = ({ token }) => {
  const state = useLoad(configApi.policies, token);
  return (
    <Loading state={state}>
      {(data) => (
        <div className="space-y-4">
          <Section
            title="Roles"
            hint="Super Admin comes from the admin email list; project roles are fixed in code."
          >
            <div className="space-y-2">
              {Object.entries(data.roles).map(([role, permissions]) => (
                <details key={role} className="rounded-md bg-zinc-900/60 p-3">
                  <summary className="cursor-pointer text-sm text-zinc-200">
                    {role}{' '}
                    <span className="text-zinc-500">
                      · {permissions.length} permissions
                    </span>
                  </summary>
                  <p className={`${mono} mt-2 leading-6 text-zinc-400`}>
                    {permissions.join(', ')}
                  </p>
                </details>
              ))}
            </div>
          </Section>
          <Section
            title="Data classification"
            hint="RESTRICTED facts need an explicit grant per compartment."
          >
            <p className="text-sm text-zinc-300">
              Levels: {data.sensitivity.join(', ')}
            </p>
            <p className="mt-1 text-sm text-zinc-300">
              Compartments: {data.compartments.join(', ')}
            </p>
          </Section>
          <Section
            title="Retention"
            hint={`Deleted rows are removed after ${data.retention.default_days} days unless listed here.`}
          >
            <ul className={`${mono} text-zinc-400`}>
              {Object.entries(data.retention.overrides).map(([name, days]) => (
                <li key={name}>
                  {name}: {days} days
                </li>
              ))}
            </ul>
          </Section>
          <Section title="Security posture" hint="Set in code.">
            <ul className="space-y-1">
              {data.security_flags.map((f) => (
                <li key={f.name} className="text-sm">
                  <span className={`${mono} text-zinc-200`}>{f.name}</span>{' '}
                  <span className="text-zinc-400">{String(f.value)}</span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      )}
    </Loading>
  );
};

const Settings = ({ token, initialArea }) => {
  const canManage = useAccess().can('admin:config:manage');
  const [version, setVersion] = useState(0);
  const state = useLoad(configApi.overview, token, version);
  const [area, setArea] = useState(initialArea || 'mail');
  return (
    <Loading state={state}>
      {(data) => (
        <div className="grid gap-6 md:grid-cols-[240px_minmax(0,1fr)]">
          <nav aria-label="Setting areas" className="space-y-0.5">
            {data.areas.map((a) => (
              <button
                key={a.area}
                onClick={() => setArea(a.area)}
                aria-current={a.area === area ? 'true' : undefined}
                className={`w-full rounded-md px-3 py-2 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500 ${
                  a.area === area
                    ? 'bg-indigo-600/15 text-indigo-200'
                    : 'text-zinc-300 hover:bg-zinc-800/70'
                }`}
              >
                <span className="flex items-center justify-between gap-2 text-sm">
                  {AREA_LABEL[a.area] || a.area}
                  <span className="text-xs tabular-nums text-zinc-400">
                    {a.changed ? `${a.changed} changed` : `${a.settings}`}
                  </span>
                </span>
                <span className="block text-xs text-zinc-400">
                  {AREA_HINT[a.area]}
                </span>
              </button>
            ))}
          </nav>
          <div className="min-w-0">
            {!canManage && (
              <p className="mb-3 text-sm text-zinc-400">
                View only: changing these needs the Manage Configuration
                permission.
              </p>
            )}
            <SettingsEditor
              key={area}
              token={token}
              area={area}
              canManage={canManage}
              groupLabel={(stage) =>
                area === 'meetings'
                  ? `Stage ${stage}`
                  : stage.charAt(0).toUpperCase() + stage.slice(1)
              }
              onPublished={() => setVersion((v) => v + 1)}
            />
          </div>
        </div>
      )}
    </Loading>
  );
};

const CHANGE_AREAS = [
  'meetings',
  'mail',
  'assistant',
  'dashboards',
  'cost',
  'ai_models',
];

const ChangeLog = ({ token }) => {
  const [area, setArea] = useState('');
  const [state, setState] = useState({ data: null, error: '' });
  useEffect(() => {
    let stale = false;
    configApi
      .changes(token, area)
      .then((data) => !stale && setState({ data, error: '' }))
      .catch((e) => !stale && setState({ data: null, error: errorText(e) }));
    return () => {
      stale = true;
    };
  }, [token, area]);
  return (
    <div className="space-y-4">
      <div role="group" aria-label="Area" className="flex flex-wrap gap-1.5">
        {['', ...CHANGE_AREAS].map((a) => (
          <button
            key={a || 'all'}
            aria-pressed={area === a}
            onClick={() => setArea(a)}
            className={`${badge} border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500 ${
              area === a
                ? 'border-indigo-500 bg-indigo-600/15 text-indigo-200'
                : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'
            }`}
          >
            {a ? AREA_LABEL[a] || a : 'All'}
          </button>
        ))}
      </div>
      <Section
        title="Change log"
        hint="Every published setting, prompt, rubric, admission policy and AI model change, newest first (last 200)."
      >
        <Loading state={state}>
          {(data) => <ChangeList changes={data.changes} />}
        </Loading>
      </Section>
    </div>
  );
};

const TABS = [
  ['overview', 'Overview'],
  ['settings', 'Settings'],
  ['prompts', 'Prompts'],
  ['meetings', 'Meetings pipeline'],
  ['switches', 'Switches'],
  ['policies', 'Policies'],
  ['health', 'Health'],
  ['changes', 'Change log'],
  ['approvals', 'Approvals'],
  ['transfer', 'Transfer'],
];

export default function ConfigurationPanel({ userIdToken }) {
  const canManage = useAccess().can('admin:config:manage');
  const [tab, setTab] = useState('overview');
  const [promptKey, setPromptKey] = useState(null);
  const [settingsArea, setSettingsArea] = useState(null);
  const openSettings = (area) => {
    setSettingsArea(area);
    setTab('settings');
  };
  const openPrompt = (key) => {
    setPromptKey(key);
    setTab('prompts');
  };
  return (
    <div className="space-y-4">
      <div
        role="tablist"
        aria-label="Configuration"
        className="flex w-fit max-w-full gap-1 overflow-x-auto rounded-lg bg-zinc-900 p-1"
      >
        {TABS.map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-500 ${
              tab === id
                ? 'bg-zinc-700 text-white'
                : 'text-zinc-400 hover:text-zinc-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'overview' && (
        <Overview token={userIdToken} openSettings={openSettings} />
      )}
      {tab === 'settings' && (
        <Settings token={userIdToken} initialArea={settingsArea} />
      )}
      {tab === 'prompts' && (
        <PromptsPanel userIdToken={userIdToken} initialKey={promptKey} />
      )}
      {tab === 'meetings' && (
        <PipelineSettingsPanel
          userIdToken={userIdToken}
          onOpenPrompt={openPrompt}
        />
      )}
      {tab === 'switches' && <Switches token={userIdToken} />}
      {tab === 'policies' && <Policies token={userIdToken} />}
      {tab === 'health' && <HealthPanel token={userIdToken} />}
      {tab === 'changes' && <ChangeLog token={userIdToken} />}
      {tab === 'approvals' && (
        <Approvals token={userIdToken} canManage={canManage} />
      )}
      {tab === 'transfer' && (
        <Transfer token={userIdToken} canManage={canManage} />
      )}
    </div>
  );
}
